import { Injectable } from '@angular/core';
import { AppVersionService } from './app-version.service';
import { CacheManagerService } from './cache-manager.service';
import { APP_VERSION_STORAGE_KEYS, ReloadGuardState } from '../models/app-version.model';

const RELOAD_GUARD_KEY = APP_VERSION_STORAGE_KEYS.reloadGuard;
const MAX_RELOAD_ATTEMPTS_PER_VERSION = 1;

/**
 * Absolute cap on how long the decision phase (fetch manifest/remote
 * config, compare versions, invalidate caches) can block app bootstrap.
 * Does NOT apply to the two intentional terminal states below (reloading,
 * or blocking on a force-update) — those are meant to keep the splash up.
 */
const STARTUP_TIMEOUT_MS = 8000;

const SPLASH_ELEMENT_ID = 'app-splash-screen';

type StartupAction = 'proceed' | 'reload' | 'block-update';

interface StartupDecision {
  action: StartupAction;
}

/**
 * Runs once on every app launch (wired as an APP_INITIALIZER — see
 * app.module.ts). Detects a new deployed build vs. what this webview last
 * booted with, invalidates stale static-asset caches, persists the new
 * version, and performs a single controlled reload so the new assets take
 * effect. Never blocks startup indefinitely on a hung network/cache call,
 * and never reload-loops.
 */
@Injectable({
  providedIn: 'root'
})
export class StartupService {

  constructor(
    private appVersion: AppVersionService,
    private cacheManager: CacheManagerService
  ) {}

  private log(message: string, ...rest: any[]) {
    console.log(`[Startup] ${message}`, ...rest);
  }

  private warn(message: string, ...rest: any[]) {
    console.warn(`[Startup] ${message}`, ...rest);
  }

  /**
   * Entry point for APP_INITIALIZER. Resolves once it's safe for Angular to
   * render the app. If the decision is to reload or to block on a required
   * update, this promise intentionally never resolves — the splash stays
   * up (through the reload navigation, or indefinitely until the backend
   * lifts forceUpdate) and Angular never renders app content behind it.
   */
  async init(): Promise<void> {
    const decision = await this.withTimeout(this.determineStartupAction(), STARTUP_TIMEOUT_MS);

    if (decision.action === 'reload') {
      this.log('Reloading application to pick up new build');
      this.reloadApplication();
      return new Promise<void>(() => {});
    }

    if (decision.action === 'block-update') {
      this.showForceUpdateOverlay();
      return new Promise<void>(() => {});
    }

    this.hideSplash();
  }

  private withTimeout(work: Promise<StartupDecision>, ms: number): Promise<StartupDecision> {
    const timeoutFallback = new Promise<StartupDecision>(resolve => {
      setTimeout(() => {
        this.warn(`Startup decision phase timed out after ${ms}ms — proceeding without blocking further`);
        resolve({ action: 'proceed' });
      }, ms);
    });
    return Promise.race([work, timeoutFallback]);
  }

  private async determineStartupAction(): Promise<StartupDecision> {
    const previousVersion = this.appVersion.getStoredVersion();
    this.log('Stored build version:', previousVersion || '(none — first launch or storage cleared)');

    const [manifest, remoteConfig] = await Promise.all([
      this.appVersion.fetchDeployedVersion(),
      this.appVersion.fetchRemoteConfig()
    ]);

    if (!manifest) {
      this.warn('Could not determine deployed build version (offline or manifest missing) — skipping version check for this launch');
      return { action: 'proceed' };
    }

    const currentVersion = manifest.buildVersion;
    this.log('Deployed build version:', currentVersion, 'built at', manifest.buildTime);
    if (remoteConfig) {
      this.log('Remote version config:', remoteConfig);
    }

    const firstLaunch = !previousVersion;
    if (firstLaunch) {
      this.log('First launch on this device/browser profile — storing initial build version');
      this.appVersion.setStoredVersion(currentVersion);
      return { action: 'proceed' };
    }

    const versionChanged = previousVersion !== currentVersion;
    const forceUpdateRequired = this.appVersion.isForceUpdateRequired(currentVersion, remoteConfig);

    if (!versionChanged && !forceUpdateRequired) {
      this.log('Build version unchanged — no cache invalidation needed');
      return { action: 'proceed' };
    }

    if (!versionChanged && forceUpdateRequired) {
      // Remote policy says this build is below minimumVersion, but there is
      // no newer local build for this webview to fetch (buildVersion in
      // assets/version.json hasn't moved) — reloading would just re-serve
      // the same stale build and loop pointlessly. This means the deployed
      // web assets themselves are out of date server-side, or (native
      // shell scenarios) the installed binary predates a bundled-assets
      // update. Block on a clear message instead of looping.
      this.warn(`Force update required: current build ${currentVersion} is below minimumVersion ${remoteConfig && remoteConfig.minimumVersion}, and no newer build is available locally to reload into.`);
      return { action: 'block-update' };
    }

    this.log(`Version change detected: ${previousVersion} -> ${currentVersion}`);

    const guard = this.readReloadGuard();
    if (guard && guard.version === currentVersion && guard.attempts >= MAX_RELOAD_ATTEMPTS_PER_VERSION) {
      this.warn(`Reload-loop guard tripped for version ${currentVersion} (already attempted ${guard.attempts} time(s)) — proceeding without reloading to avoid an infinite loop`);
      // Still invalidate caches and store the version so we're internally
      // consistent, we just stop trying to reload for this version.
      await this.cacheManager.invalidateStaticAssetCaches();
      this.appVersion.setStoredVersion(currentVersion);
      return { action: 'proceed' };
    }

    this.writeReloadGuard(currentVersion, guard);

    this.log('Invalidating static asset caches (Cache Storage / service worker)');
    await this.cacheManager.invalidateStaticAssetCaches();

    // Persist the new version BEFORE reloading so that, after reload, the
    // stored version already matches the deployed version and the check
    // above short-circuits — this is what actually prevents the loop.
    this.appVersion.setStoredVersion(currentVersion);

    return { action: 'reload' };
  }

  private readReloadGuard(): ReloadGuardState | null {
    try {
      const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
      return raw ? JSON.parse(raw) as ReloadGuardState : null;
    } catch (e) {
      return null;
    }
  }

  private writeReloadGuard(version: string, existing: ReloadGuardState | null): void {
    try {
      const attempts = existing && existing.version === version ? existing.attempts + 1 : 1;
      const state: ReloadGuardState = { version, attempts };
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(state));
    } catch (e) {
      this.warn('Unable to persist reload guard (sessionStorage unavailable)', e);
    }
  }

  private reloadApplication(): void {
    try {
      window.location.reload();
    } catch (e) {
      this.warn('window.location.reload() failed', e);
    }
  }

  private hideSplash(): void {
    const el = document.getElementById(SPLASH_ELEMENT_ID);
    if (!el) {
      return;
    }
    el.classList.add('app-splash-hidden');
    setTimeout(() => el.parentNode && el.parentNode.removeChild(el), 400);
  }

  /**
   * Replaces the splash content with a non-dismissible "update required"
   * message and leaves it up (init() never resolves in this branch, so
   * Angular never renders app content behind it either). Reversible by the
   * backend simply flipping forceUpdate off in the remote config — the
   * next launch will re-check and proceed normally.
   */
  private showForceUpdateOverlay(): void {
    const el = document.getElementById(SPLASH_ELEMENT_ID);
    if (!el) {
      return;
    }
    el.innerHTML = `
      <div style="max-width:280px;text-align:center;padding:0 24px;font-family:sans-serif;">
        <h3 style="margin:0 0 8px;color:#222;">Update Required</h3>
        <p style="margin:0;color:#555;font-size:14px;line-height:1.4;">
          A new version of this app is required to continue. Please update to the latest version.
        </p>
      </div>
    `;
  }
}
